import React, { useState } from 'react';
import { Bell, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { mediaAlertsAPI } from '../services/api';

const MediaAlertPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    user_name: '',
    user_email: '',
    user_phone: '',
    monitored_names: '',
    monitored_keywords: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convert comma-separated strings to arrays
      const alertData = {
        user_name: formData.user_name,
        user_email: formData.user_email,
        user_phone: formData.user_phone || undefined,
        monitored_names: formData.monitored_names.split(',').map(n => n.trim()).filter(n => n),
        monitored_keywords: formData.monitored_keywords.split(',').map(k => k.trim()).filter(k => k),
        alert_preferences: {
          email: true,
          sms: false
        }
      };

      await mediaAlertsAPI.create(alertData);

      toast({
        title: "Media Alert Created!",
        description: "You'll receive email notifications when your name appears in Express Advocate articles."
      });

      // Reset form
      setFormData({
        user_name: '',
        user_email: '',
        user_phone: '',
        monitored_names: '',
        monitored_keywords: ''
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create media alert. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-16">
      <div className="text-center mb-12">
        <Bell size={48} className="mx-auto mb-4 text-gray-600" />
        <h1 className="text-4xl font-serif font-bold mb-4">
          Personal Media Alert System
        </h1>
        <p className="text-gray-600 text-lg">
          Get instant notifications when your name appears in Express Advocate articles
        </p>
      </div>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>How It Works</CardTitle>
          <CardDescription>
            Stay ahead of biased media coverage
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <div>
                <h3 className="font-semibold mb-1">Register Your Name</h3>
                <p className="text-sm text-gray-600">Enter your name, business name, or any keywords you want to monitor</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">
                2
              </div>
              <div>
                <h3 className="font-semibold mb-1">We Monitor Daily</h3>
                <p className="text-sm text-gray-600">Our system checks Express Advocate every day for mentions of your name</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">
                3
              </div>
              <div>
                <h3 className="font-semibold mb-1">Instant Email Alert</h3>
                <p className="text-sm text-gray-600">Get notified immediately when your name appears in an article</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">
                4
              </div>
              <div>
                <h3 className="font-semibold mb-1">Quick Response</h3>
                <p className="text-sm text-gray-600">Submit your counter-story within 24 hours before the damage spreads</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Create Media Alert</CardTitle>
          <CardDescription>
            Never be caught off guard by biased media coverage again
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="user_name">Your Name *</Label>
              <Input
                id="user_name"
                value={formData.user_name}
                onChange={(e) => setFormData({...formData, user_name: e.target.value})}
                placeholder="John Smith"
                required
              />
            </div>

            <div>
              <Label htmlFor="user_email">Email Address *</Label>
              <Input
                id="user_email"
                type="email"
                value={formData.user_email}
                onChange={(e) => setFormData({...formData, user_email: e.target.value})}
                placeholder="your.email@example.com"
                required
              />
            </div>

            <div>
              <Label htmlFor="user_phone">Phone Number (Optional)</Label>
              <Input
                id="user_phone"
                type="tel"
                value={formData.user_phone}
                onChange={(e) => setFormData({...formData, user_phone: e.target.value})}
                placeholder="+61 400 000 000"
              />
              <p className="text-xs text-gray-500 mt-1">SMS alerts coming soon</p>
            </div>

            <div>
              <Label htmlFor="monitored_names">Names to Monitor *</Label>
              <Input
                id="monitored_names"
                value={formData.monitored_names}
                onChange={(e) => setFormData({...formData, monitored_names: e.target.value})}
                placeholder="John Smith, Smith & Co, JS Enterprises"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Separate multiple names with commas</p>
            </div>

            <div>
              <Label htmlFor="monitored_keywords">Additional Keywords (Optional)</Label>
              <Input
                id="monitored_keywords"
                value={formData.monitored_keywords}
                onChange={(e) => setFormData({...formData, monitored_keywords: e.target.value})}
                placeholder="fraud, scandal, investigation"
              />
              <p className="text-xs text-gray-500 mt-1">Alert me if these words appear near my name</p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex gap-3">
              <AlertCircle className="text-yellow-600 flex-shrink-0" size={20} />
              <div className="text-sm text-gray-700">
                <strong>Privacy Notice:</strong> Your information is only used for monitoring purposes. We never sell or share your data.
              </div>
            </div>

            <Button type="submit" className="w-full" size="lg" disabled={loading}>
              {loading ? 'Creating Alert...' : 'Create Media Alert'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default MediaAlertPage;
